"use client"

import { ChevronDown, Menu, Moon, Sun, X } from "lucide-react"
import { observer } from "mobx-react-lite"
import * as React from "react"
import { cn } from "@/lib/utils"
import type { NavbarDropdownStoreType } from "@/stores/NavbarDropdownStore"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { Button } from "./ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"

export interface MenuItem {
  label: string
  onClick?: () => void
  href?: string
  icon?: React.ReactNode
  children?: MenuItem[]
  active?: boolean
}

export interface IconItem {
  icon?: React.ReactNode
  image?: string
  label?: string
  onClick?: () => void
  children?: MenuItem[]
}

export interface AppIconConfig {
  icon?: React.ReactNode
  image?: string
  label?: string
  position?: "bottom-left" | "bottom-right"
  children?: MenuItem[]
  /**
   * Handler called when the logo/icon is clicked.
   * Typically used for navigation (e.g., navigating to home page).
   */
  onClick?: () => void
}

export interface NavbarDropdownConfig {
  store: NavbarDropdownStoreType
  onSelect: (optionId: string) => void
  className?: string
}

export interface LanguageSelectorConfig {
  currentLanguage: string
  languages: Array<{ code: string; label: string }>
  onLanguageChange: (languageCode: string) => void
}

export interface UIShellProps {
  logo?: AppIconConfig
  appName?: string
  menuItems?: MenuItem[]
  navbarDropdown?: NavbarDropdownConfig
  languageSelector?: LanguageSelectorConfig
  rightIcons?: IconItem[]
  onThemeToggle?: () => void
  children?: React.ReactNode
  footer?: React.ReactNode
  showFooter?: boolean
  appIcon?: AppIconConfig
  showAppIcon?: boolean
  className?: string
  navbarClassName?: string
}

const MenuItemRenderer: React.FC<{
  item: MenuItem
  onItemClick?: () => void
}> = ({ item, onItemClick }) => {
  const handleClick = () => {
    if (item.onClick) {
      item.onClick()
    }
    if (onItemClick) {
      onItemClick()
    }
  }

  if (item.children && item.children.length > 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "flex items-center gap-2 cursor-pointer justify-start lg:w-auto w-full",
              item.active && "bg-accent text-accent-foreground",
            )}
          >
            {item.icon}
            {item.label}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {item.children.map((child, idx) => (
            <DropdownMenuItem
              key={`${item.label}-child-${idx}-${child.label}`}
              onClick={() => {
                if (child.onClick) child.onClick()
                if (onItemClick) onItemClick()
              }}
              className="flex items-center gap-2 cursor-pointer"
            >
              {child.icon}
              {child.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <Button
      variant="ghost"
      onClick={handleClick}
      className={cn(
        "flex items-center gap-2 cursor-pointer justify-start lg:w-auto w-full",
        item.active && "bg-accent text-accent-foreground",
      )}
    >
      {item.icon}
      {item.label}
    </Button>
  )
}

const IconItemRenderer: React.FC<{ item: IconItem }> = ({ item }) => {
  if (item.children && item.children.length > 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative cursor-pointer">
            {item.image ? (
              <Avatar className="h-8 w-8">
                <AvatarImage src={item.image} alt={item.label || "User"} />
                <AvatarFallback>{item.label?.[0] || "U"}</AvatarFallback>
              </Avatar>
            ) : (
              item.icon
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {item.children.map((child, idx) => (
            <DropdownMenuItem
              key={`icon-child-${idx}-${child.label}`}
              onClick={child.onClick}
              className="flex items-center gap-2 cursor-pointer"
            >
              {child.icon}
              {child.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <Button variant="ghost" size="icon" onClick={item.onClick} className="relative cursor-pointer">
      {item.image ? (
        <Avatar className="h-8 w-8">
          <AvatarImage src={item.image} alt={item.label || "User"} />
          <AvatarFallback>{item.label?.[0] || "U"}</AvatarFallback>
        </Avatar>
      ) : (
        item.icon
      )}
    </Button>
  )
}

const NavbarDropdownRenderer: React.FC<NavbarDropdownConfig> = observer(
  ({ store, onSelect, className }) => {
    const handleSelect = (optionId: string) => {
      store.setSelectedOption(optionId)
      onSelect(optionId)
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className={cn("min-w-[150px] justify-between", className)}>
            <span className="truncate">{store.displayLabel}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[150px]">
          {store.options.map((option) => (
            <DropdownMenuItem
              key={option.id}
              onClick={() => handleSelect(option.id)}
              disabled={option.disabled}
              className={cn("cursor-pointer", store.isOptionSelected(option.id) && "bg-accent")}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  },
)

NavbarDropdownRenderer.displayName = "NavbarDropdownRenderer"

// Language Selector Component
const LanguageSelector: React.FC<LanguageSelectorConfig> = ({
  currentLanguage,
  languages,
  onLanguageChange,
}) => {
  const currentLang = languages.find((lang) => lang.code === currentLanguage)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 px-3 cursor-pointer uppercase font-medium">
          {currentLang?.code || currentLanguage}
          <ChevronDown className="ml-1 h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => onLanguageChange(lang.code)}
            className={cn("cursor-pointer", currentLanguage === lang.code && "bg-accent")}
          >
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

LanguageSelector.displayName = "LanguageSelector"

// Theme Toggle - Simple version with callback
const ThemeToggle: React.FC<{ onToggle: () => void }> = ({ onToggle }) => {
  return (
    <Button variant="ghost" size="icon" className="cursor-pointer" onClick={onToggle}>
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  )
}

ThemeToggle.displayName = "ThemeToggle"

export const UIShell: React.FC<UIShellProps> = ({
  logo,
  appName,
  menuItems = [],
  navbarDropdown,
  languageSelector,
  rightIcons = [],
  onThemeToggle,
  children,
  footer,
  showFooter = true,
  appIcon,
  showAppIcon = true,
  className,
  navbarClassName,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)

  return (
    <div className={cn("flex h-screen flex-col", className)}>
      {/* Navbar */}
      <header className={cn("sticky top-0 z-50 w-full border-b bg-background", navbarClassName)}>
        <div className="flex h-14 items-center px-2 sm:px-4 gap-1 sm:gap-2">
          {/* Mobile Menu Button - Toggle icon between Menu and X */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden cursor-pointer shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          {/* Logo and App Name - Clickable to navigate home */}
          <button
            type="button"
            className="flex items-center gap-2 cursor-pointer shrink-0 bg-transparent border-0 hover:opacity-80 transition-opacity"
            onClick={() => logo?.onClick?.()}
            aria-label="Navigate to home"
          >
            {logo &&
              (logo.image ? (
                <Avatar className="h-8 w-8">
                  <AvatarImage src={logo.image} alt={logo.label || "App"} />
                  <AvatarFallback>{logo.label?.[0] || "A"}</AvatarFallback>
                </Avatar>
              ) : (
                logo.icon
              ))}
            {appName && (
              <span className="font-semibold text-base sm:text-lg truncate">{appName}</span>
            )}
          </button>

          {/* Desktop Menu Items - Hidden on mobile/tablet */}
          <nav
            className="navbar-content hidden lg:flex items-center flex-1"
            aria-label="Main navigation"
          >
            {menuItems.map((item, idx) => (
              <MenuItemRenderer key={`menu-${idx}-${item.label}`} item={item} />
            ))}
          </nav>

          {/* Right Side Container: Dropdown + Language + Theme Toggle + Icons */}
          <div className="flex items-center gap-1 sm:gap-2 ml-auto shrink-0">
            {/* Optional Navbar Dropdown - responsive sizing */}
            {navbarDropdown && (
              <div className="hidden sm:block">
                <NavbarDropdownRenderer
                  store={navbarDropdown.store}
                  onSelect={navbarDropdown.onSelect}
                  className={cn("min-w-[120px] sm:min-w-[150px]", navbarDropdown.className)}
                />
              </div>
            )}

            {/* Language Selector - responsive sizing */}
            {languageSelector && (
              <LanguageSelector
                currentLanguage={languageSelector.currentLanguage}
                languages={languageSelector.languages}
                onLanguageChange={languageSelector.onLanguageChange}
              />
            )}

            {/* Theme Toggle */}
            {onThemeToggle && <ThemeToggle onToggle={onThemeToggle} />}

            {/* Right Icons */}
            {rightIcons.map((item, idx) => (
              <IconItemRenderer key={`right-icon-${idx}-${item.label}`} item={item} />
            ))}
          </div>
        </div>

        {/* Mobile Menu - Overlay below navbar */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-14 left-0 right-0 border-b bg-background shadow-lg z-40">
            <nav className="flex flex-col py-3 px-4 gap-1" aria-label="Mobile navigation">
              {menuItems.map((item, idx) => (
                <MenuItemRenderer
                  key={`mobile-menu-${idx}-${item.label}`}
                  item={item}
                  onItemClick={() => setMobileMenuOpen(false)}
                />
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Content Area */}
      <main className="flex-1 overflow-y-auto">
        {children}

        {/* Footer - Non-sticky, at the end of content */}
        {showFooter && footer && (
          <footer className="border-t bg-background">
            <div className="container py-4">{footer}</div>
          </footer>
        )}
      </main>

      {/* Optional App Icon */}
      {showAppIcon && appIcon && (
        <div
          className={cn(
            "fixed bottom-4 z-40",
            appIcon.position === "bottom-right" ? "right-4" : "left-4",
          )}
        >
          {appIcon.children && appIcon.children.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" className="h-12 w-12 rounded-full shadow-lg cursor-pointer">
                  {appIcon.image ? (
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={appIcon.image} alt={appIcon.label || "App"} />
                      <AvatarFallback>{appIcon.label?.[0] || "A"}</AvatarFallback>
                    </Avatar>
                  ) : (
                    appIcon.icon
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={appIcon.position === "bottom-right" ? "end" : "start"}
                side="top"
              >
                {appIcon.children.map((child, idx) => (
                  <DropdownMenuItem
                    key={`app-icon-child-${idx}-${child.label}`}
                    onClick={child.onClick}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    {child.icon}
                    {child.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              size="icon"
              className="h-12 w-12 rounded-full shadow-lg cursor-pointer"
              onClick={() => {}}
            >
              {appIcon.image ? (
                <Avatar className="h-10 w-10">
                  <AvatarImage src={appIcon.image} alt={appIcon.label || "App"} />
                  <AvatarFallback>{appIcon.label?.[0] || "A"}</AvatarFallback>
                </Avatar>
              ) : (
                appIcon.icon
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
